import { isUndefined, keys, omitBy, pick } from 'lodash-es';
import { useRouter } from 'next/router';

type ParsedQuery = Record<string, any>;

interface QueryParams {
  [key: string]: string | string[] | undefined;
}

interface UseQueryParamsOptions<T extends ParsedQuery> {
  queryParamKeys?: (keyof T)[];
  defaultValue?: Partial<T>;
}

export const useCustomQueryParams = <T extends ParsedQuery>({
  queryParamKeys,
  defaultValue,
}: UseQueryParamsOptions<T>) => {
  const router = useRouter();

  const replace = (newQueryParams: Partial<T>) => {
    const currentQueryParams = router.query as QueryParams;
    const updatedQueryParams = {
      ...currentQueryParams,
      ...pick(newQueryParams, queryParamKeys || keys(newQueryParams)),
    };
    router.replace({ query: updatedQueryParams }, undefined, { shallow: true });
  };

  const push = (newQueryParams: Partial<T>, options?: { scroll?: boolean; shallow?: boolean }) => {
    const currentQueryParams = router.query as QueryParams;
    const updatedQueryParams = {
      ...currentQueryParams,
      ...pick(newQueryParams, queryParamKeys || keys(newQueryParams)),
    };
    router.push({ query: updatedQueryParams }, undefined, { shallow: true, ...options });
  };

  const queryParams: QueryParams = router.query as QueryParams;

  const _mergedQueryParams = omitBy(
    {
      ...defaultValue,
      ...queryParams,
    },
    isUndefined,
  );

  const mergedQueryParams = pick(_mergedQueryParams, queryParamKeys || keys(_mergedQueryParams));

  return {
    queryParams: mergedQueryParams as T,
    replace,
    push,
  };
};
